/* ============================================================================
   MALSERA — ms-collection.js
   Densidad, carga infinita, barra flotante y carrusel de tarjeta.

   Los carruseles de las tarjetas son desplazamiento NATIVO con scroll-snap:
   aquí no se ejecuta nada durante el gesto. Este archivo solo sincroniza los
   puntos y activa las imágenes diferidas al primer contacto.
   ============================================================================ */

const MS_CLAVE_DENSIDAD = 'ms-densidad';
const MS_LOTES_AUTO = 3;

class MsColeccion extends HTMLElement {
  connectedCallback() {
    this.rejilla = this.querySelector('[data-ms-rejilla]');
    this.anuncio = this.querySelector('[data-ms-anuncio]');
    this.flotante = this.querySelector('[data-ms-flotante]');
    this.cabecera = this.querySelector('.ms-col-cabecera');
    this.lotes = 0;

    this.conectarDensidad();
    this.conectarTarjetas(this);
    this.conectarFlotante();
    this.conectarCarga();
  }

  /* --- Densidad ------------------------------------------------------------
     La elección se guarda y se aplica en las siguientes colecciones. Cambiarla
     no recarga ni vuelve a pedir productos: solo cambia una clase.
     ---------------------------------------------------------------------- */

  conectarDensidad() {
    if (!this.rejilla) return;

    this.grupos = [...this.querySelectorAll('[data-ms-densidad]')];
    const escritorio = window.matchMedia('(min-width: 1024px)');

    const guardado = this.leerGuardado();
    this.aplicarDensidad(guardado[escritorio.matches ? 'escritorio' : 'movil'], false);

    this.grupos.forEach((grupo) => {
      grupo.querySelectorAll('[data-ms-cols]').forEach((boton) => {
        boton.addEventListener('click', () => this.aplicarDensidad(boton.dataset.msCols, true));
      });
    });

    // Al cambiar de formato se recupera la preferencia de ESE formato: 3 o 2
    // columnas en escritorio no significan lo mismo que 2 o 1 en móvil.
    escritorio.addEventListener('change', (evento) => {
      const g = this.leerGuardado();
      this.aplicarDensidad(g[evento.matches ? 'escritorio' : 'movil'], false);
    });
  }

  leerGuardado() {
    const porDefecto = { escritorio: '3', movil: '2' };
    try {
      return { ...porDefecto, ...JSON.parse(localStorage.getItem(MS_CLAVE_DENSIDAD) || '{}') };
    } catch (e) {
      return porDefecto;
    }
  }

  aplicarDensidad(columnas, guardar) {
    if (!columnas) return;

    // Se conserva la posición relativa al primer producto visible para que el
    // cambio de densidad no provoque un salto de scroll.
    const referencia = [...this.rejilla.children].find(
      (c) => c.getBoundingClientRect().bottom > 0
    );
    const antes = referencia ? referencia.getBoundingClientRect().top : null;

    this.rejilla.dataset.cols = columnas;

    // Los dos selectores comparten estado.
    this.grupos.forEach((grupo) => {
      grupo.querySelectorAll('[data-ms-cols]').forEach((boton) => {
        boton.setAttribute('aria-checked', boton.dataset.msCols === columnas ? 'true' : 'false');
      });
    });

    // El sizes tiene que reflejar la densidad o se sirven imágenes pequeñas
    // estiradas al pasar a 1 o 2 columnas.
    const sizes =
      columnas === '1' ? '100vw' : columnas === '2' ? '50vw' : '(min-width: 1024px) 33vw, 50vw';
    this.rejilla.querySelectorAll('.ms-tarjeta__img').forEach((img) => {
      img.sizes = sizes;
    });

    if (referencia && antes !== null) {
      const despues = referencia.getBoundingClientRect().top;
      window.scrollBy({ top: despues - antes, behavior: 'instant' });
    }

    if (guardar) {
      try {
        const g = this.leerGuardado();
        g[window.matchMedia('(min-width: 1024px)').matches ? 'escritorio' : 'movil'] = columnas;
        localStorage.setItem(MS_CLAVE_DENSIDAD, JSON.stringify(g));
      } catch (e) {
        /* Navegador con almacenamiento bloqueado: se usa el valor por defecto. */
      }
    }
  }

  /* --- Carrusel de cada tarjeta -------------------------------------------- */

  conectarTarjetas(raiz) {
    raiz.querySelectorAll('[data-ms-tarjeta]').forEach((tarjeta) => {
      const pista = tarjeta.querySelector('[data-ms-tarjeta-pista]');
      const puntos = [...tarjeta.querySelectorAll('.ms-tarjeta__punto')];
      if (!pista) return;

      // Las imágenes 2..12 solo se piden cuando alguien toca ESTA tarjeta.
      // Sin esto serían 12 imágenes × 12 tarjetas en la primera pantalla.
      const despertar = () => {
        pista.querySelectorAll('img[data-src]').forEach((img) => {
          img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        });
      };
      ['pointerenter', 'touchstart', 'focusin'].forEach((evento) => {
        tarjeta.addEventListener(evento, despertar, { once: true, passive: true });
      });

      if (!puntos.length) return;

      let pendiente = false;
      pista.addEventListener(
        'scroll',
        () => {
          if (pendiente) return;
          pendiente = true;
          requestAnimationFrame(() => {
            const indice = Math.round(pista.scrollLeft / pista.clientWidth);
            puntos.forEach((p, i) => p.classList.toggle('is-active', i === indice));
            const prev = tarjeta.querySelector('[data-ms-tarjeta-prev]');
            const next = tarjeta.querySelector('[data-ms-tarjeta-next]');
            if (prev) prev.disabled = indice <= 0;
            if (next) next.disabled = indice >= puntos.length - 1;
            pendiente = false;
          });
        },
        { passive: true }
      );

      tarjeta.querySelector('[data-ms-tarjeta-prev]')?.addEventListener('click', (e) => {
        e.preventDefault();
        despertar();
        pista.scrollBy({ left: -pista.clientWidth, behavior: 'smooth' });
      });
      tarjeta.querySelector('[data-ms-tarjeta-next]')?.addEventListener('click', (e) => {
        e.preventDefault();
        despertar();
        pista.scrollBy({ left: pista.clientWidth, behavior: 'smooth' });
      });
    });
  }

  /* --- Barra flotante ------------------------------------------------------- */

  conectarFlotante() {
    if (!this.flotante || !this.cabecera) return;

    const mostrar = (si) => {
      this.flotante.classList.toggle('is-visible', si);
      this.flotante.setAttribute('aria-hidden', si ? 'false' : 'true');
    };

    // Observadores dedicados, no listeners de scroll.
    this.obsCabecera = new IntersectionObserver(
      ([e]) => {
        this.cabeceraFuera = !e.isIntersecting;
        mostrar(this.cabeceraFuera && !this.footerDentro);
      },
      { threshold: 0 }
    );
    this.obsCabecera.observe(this.cabecera);

    const pie = document.querySelector('.shopify-section-group-footer-group, footer');
    if (pie) {
      this.obsPie = new IntersectionObserver(
        ([e]) => {
          // Se esconde al llegar el footer: si no, tapa los enlaces legales y
          // el formulario, que es justo donde se va a pulsar.
          this.footerDentro = e.isIntersecting;
          mostrar(this.cabeceraFuera && !this.footerDentro);
        },
        { threshold: 0 }
      );
      this.obsPie.observe(pie);
    }
  }

  /* --- Carga infinita ------------------------------------------------------- */

  conectarCarga() {
    this.boton = this.querySelector('[data-ms-mas]');
    if (!this.boton) return;

    this.boton.addEventListener('click', (evento) => {
      evento.preventDefault();
      this.traerLote();
    });

    // Centinela: el lote siguiente se pide 400px antes de llegar al final.
    this.centinela = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        if (this.lotes >= MS_LOTES_AUTO) return; // después, botón manual
        this.traerLote();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.centinela.observe(this.boton);
  }

  async traerLote() {
    if (this.cargando || !this.boton) return;
    const url = this.boton.dataset.siguiente;
    if (!url) return;

    this.cargando = true;
    this.boton.classList.add('is-loading');
    const esqueletos = this.pintarEsqueletos();

    try {
      const respuesta = await fetch(url);
      if (!respuesta.ok) throw new Error('red');

      const html = new DOMParser().parseFromString(await respuesta.text(), 'text/html');
      const nuevas = [...html.querySelectorAll('[data-ms-rejilla] > .ms-tarjeta')];
      const siguiente = html.querySelector('[data-ms-mas]');

      esqueletos.forEach((e) => e.remove());

      const fragmento = document.createDocumentFragment();
      nuevas.forEach((t) => {
        // Los lotes siguientes entran con fade y sin cascada: con cascada, al
        // hacer scroll rápido se ve una escalera de tarjetas llegando tarde.
        t.classList.add('ms-anim', 'ms-anim--fade');
        t.style.removeProperty('--animation-order');
        fragmento.appendChild(t);
      });
      this.rejilla.appendChild(fragmento);

      this.conectarTarjetas(this.rejilla);
      this.aplicarDensidad(this.rejilla.dataset.cols, false);
      document.dispatchEvent(new CustomEvent('ms:contenido-nuevo'));

      if (this.anuncio) this.anuncio.textContent = `${nuevas.length} productos más cargados`;
      this.lotes += 1;

      // La URL se actualiza para recuperar la posición al volver de una ficha.
      history.replaceState({}, '', url);

      if (siguiente) {
        this.boton.dataset.siguiente = siguiente.dataset.siguiente;
        this.boton.href = siguiente.getAttribute('href');
      } else {
        this.terminar();
      }
    } catch (fallo) {
      esqueletos.forEach((e) => e.remove());
      this.mostrarError();
    } finally {
      this.cargando = false;
      this.boton?.classList.remove('is-loading');
    }
  }

  pintarEsqueletos() {
    const lista = [];
    for (let i = 0; i < 3; i++) {
      const e = document.createElement('div');
      e.className = 'ms-esqueleto';
      this.rejilla.appendChild(e);
      lista.push(e);
    }
    return lista;
  }

  terminar() {
    this.centinela?.disconnect();
    const fin = document.createElement('p');
    fin.className = 'ms-col-fin';
    fin.textContent = 'No hay más productos';
    this.boton.replaceWith(fin);
    this.boton = null;
  }

  mostrarError() {
    // Lo ya cargado se conserva: solo se avisa y se ofrece reintentar.
    let aviso = this.querySelector('[data-ms-col-error]');
    if (!aviso) {
      aviso = document.createElement('p');
      aviso.className = 'ms-error';
      aviso.setAttribute('data-ms-col-error', '');
      this.boton.before(aviso);
    }
    aviso.textContent = 'No hemos podido cargar más productos. Vuelve a intentarlo.';
    this.centinela?.disconnect();
  }

  disconnectedCallback() {
    this.centinela?.disconnect();
    this.obsCabecera?.disconnect();
    this.obsPie?.disconnect();
  }
}

customElements.define('ms-coleccion', MsColeccion);
